import React from 'react';
import { FontFamilySelector } from '../FontSelector';
import { ColorPicker } from '../ColorPicker';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Check, ExternalLink } from 'lucide-react';

interface ElementPropertiesProps {
  selectedElement: any;
  onUpdateElement: (id: string, properties: any) => void;
}

export const ElementProperties: React.FC<ElementPropertiesProps> = ({
  selectedElement,
  onUpdateElement,
}) => {
  if (!selectedElement) return null;

  const updateProperty = (property: string, value: any) => {
    onUpdateElement(selectedElement.id, { [property]: value });
  };

  const renderTextProperties = () => (
    <>
      <div className="space-y-4">
        <div>
          <Label>Content</Label>
          <Textarea
            value={selectedElement.content || ''}
            onChange={(e) => updateProperty('content', e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Text Color</Label>
            <ColorPicker
              color={selectedElement.color || '#000000'}
              onChange={(value) => updateProperty('color', value)}
            />
          </div>
          <div>
            <Label>Background</Label>
            <ColorPicker
              color={selectedElement.backgroundColor || 'transparent'}
              onChange={(value) => updateProperty('backgroundColor', value)}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FontFamilySelector
              value={selectedElement.fontFamily || 'Arial, sans-serif'}
              onChange={(value) => updateProperty('fontFamily', value)}
              label="Font Family"
            />
          </div>
          <div>
            <Label>Font Size</Label>
            <Input
              type="number"
              value={selectedElement.fontSize || 16}
              onChange={(e) => updateProperty('fontSize', parseInt(e.target.value))}
              className="mt-1"
              min={8}
              max={72}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={selectedElement.fontWeight === 'bold' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty('fontWeight', selectedElement.fontWeight === 'bold' ? 'normal' : 'bold')}
            className="w-full"
          >
            Bold
          </Button>
          <Button
            variant={selectedElement.fontStyle === 'italic' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty('fontStyle', selectedElement.fontStyle === 'italic' ? 'normal' : 'italic')}
            className="w-full"
          >
            Italic
          </Button>
          <Button
            variant={selectedElement.textDecoration === 'underline' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateProperty('textDecoration', selectedElement.textDecoration === 'underline' ? 'none' : 'underline')}
            className="w-full"
          >
            Underline
          </Button>
        </div>
        
        <div>
          <Label>Text Align</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <Button
              variant={selectedElement.textAlign === 'left' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateProperty('textAlign', 'left')}
              className="w-full"
            >
              Left
            </Button>
            <Button
              variant={selectedElement.textAlign === 'center' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateProperty('textAlign', 'center')}
              className="w-full"
            >
              Center
            </Button>
            <Button
              variant={selectedElement.textAlign === 'right' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateProperty('textAlign', 'right')}
              className="w-full"
            >
              Right
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  const renderImageProperties = () => (
    <>
      <div className="space-y-4">
        <div>
          <Label>Image URL</Label>
          <Input
            value={selectedElement.src || ''}
            onChange={(e) => updateProperty('src', e.target.value)}
            className="mt-1"
            placeholder="https://example.com/image.jpg"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Width</Label>
            <Input
              type="number"
              value={selectedElement.width || 200}
              onChange={(e) => updateProperty('width', parseInt(e.target.value))}
              className="mt-1"
              min={10}
            />
          </div>
          <div>
            <Label>Height</Label>
            <Input
              type="number"
              value={selectedElement.height || 150}
              onChange={(e) => updateProperty('height', parseInt(e.target.value))}
              className="mt-1"
              min={10}
            />
          </div>
        </div>
        
        <div>
          <Label>Alt Text</Label>
          <Input
            value={selectedElement.alt || ''}
            onChange={(e) => updateProperty('alt', e.target.value)}
            className="mt-1"
            placeholder="Image description"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Border Radius</Label>
            <Input
              type="number"
              value={selectedElement.borderRadius || 0}
              onChange={(e) => updateProperty('borderRadius', parseInt(e.target.value))}
              className="mt-1"
              min={0}
              max={100}
            />
          </div>
          <div className="flex items-end pb-1">
            <div className="flex items-center space-x-2">
              <Switch
                checked={selectedElement.link ? true : false}
                onCheckedChange={(checked) => updateProperty('link', checked ? '#' : '')}
              />
              <Label>Make Clickable</Label>
            </div>
          </div>
        </div>
        
        {selectedElement.link && (
          <div>
            <Label>Link URL</Label>
            <Input
              value={selectedElement.link || ''}
              onChange={(e) => updateProperty('link', e.target.value)}
              className="mt-1"
              placeholder="https://example.com"
            />
          </div>
        )}
      </div>
    </>
  );

  const renderButtonProperties = () => (
    <>
      <div className="space-y-4">
        <div>
          <Label>Button Text</Label>
          <Input
            value={selectedElement.text || 'Button'}
            onChange={(e) => updateProperty('text', e.target.value)}
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Link URL</Label>
          <Input
            value={selectedElement.href || '#'}
            onChange={(e) => updateProperty('href', e.target.value)}
            className="mt-1"
            placeholder="https://example.com"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Button Color</Label>
            <ColorPicker
              color={selectedElement.backgroundColor || '#4F46E5'}
              onChange={(value) => updateProperty('backgroundColor', value)}
            />
          </div>
          <div>
            <Label>Text Color</Label>
            <ColorPicker
              color={selectedElement.color || '#FFFFFF'}
              onChange={(value) => updateProperty('color', value)}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Border Radius</Label>
            <Input
              type="number"
              value={selectedElement.borderRadius || 4}
              onChange={(e) => updateProperty('borderRadius', parseInt(e.target.value))}
              className="mt-1"
              min={0}
              max={50}
            />
          </div>
          <div>
            <Label>Button Style</Label>
            <Select
              value={selectedElement.style || 'filled'}
              onValueChange={(value) => updateProperty('style', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="filled">Filled</SelectItem>
                <SelectItem value="outline">Outline</SelectItem>
                <SelectItem value="ghost">Ghost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Padding X</Label>
            <Input
              type="number"
              value={selectedElement.paddingX || 16}
              onChange={(e) => updateProperty('paddingX', parseInt(e.target.value))}
              className="mt-1"
              min={0}
              max={50}
            />
          </div>
          <div>
            <Label>Padding Y</Label>
            <Input
              type="number"
              value={selectedElement.paddingY || 8}
              onChange={(e) => updateProperty('paddingY', parseInt(e.target.value))}
              className="mt-1"
              min={0}
              max={50}
            />
          </div>
        </div>
      </div>
    </>
  );

  // Render different properties based on element type
  switch (selectedElement.type) {
    case 'text':
    case 'heading':
    case 'paragraph':
      return renderTextProperties();
    case 'image':
      return renderImageProperties();
    case 'button':
      return renderButtonProperties();
    default:
      return <div>Select an element to edit its properties</div>;
  }
}; 